import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCMS } from '../../contexts/CMSContext';

function Header({ currentUser, logout, setSidebarOpen, setActiveSection }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { unreadRequests } = useCMS();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 mx-auto sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Mobile menu button */}
            <button
              className="inline-flex justify-center items-center mr-2 -ml-2 w-12 h-12 text-gray-500 rounded-md lg:hidden hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
              onClick={() => setSidebarOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <h1 className="text-2xl font-semibold text-gray-900">CMS</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Notification Badge */}
            {unreadRequests > 0 && (
              <div className="relative">
                <button
                  onClick={() => {
                    // Navigate to contact requests section
                    if (setActiveSection) {
                      setActiveSection('contact-requests');
                    }
                  }}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-500 rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                >
                  <svg className="mr-2 -ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 7l1-1h5.586a1 1 0 01.707.293L16 11v6a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
                  </svg>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-white text-red-600">
                    {unreadRequests}
                  </span>
                  <span className="ml-2">New Requests</span>
                </button>
              </div>
            )}
            
            {/* View Website Button */}
            <button
              onClick={() => window.open('/', '_blank')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <svg className="mr-2 -ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M7 7l10 10M17 7v4" />
              </svg>
              View Website
            </button>
            
            {/* User dropdown */}
            <div className="relative">
              <button
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <img
                  className="w-8 h-8 rounded-full"
                  src={currentUser?.photoURL || 'https://via.placeholder.com/32'}
                  alt={currentUser?.displayName || 'User'}
                />
                <span className="hidden ml-2 font-medium text-gray-700 sm:block">
                  {currentUser?.displayName || 'Admin'}
                </span>
                <svg className="ml-1 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {dropdownOpen && (
                <div className="absolute right-0 z-50 mt-2 w-48 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right focus:outline-none">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      <p className="font-medium">{currentUser?.displayName}</p>
                      <p className="text-xs text-gray-500">{currentUser?.email}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/');
                      }}
                      className="block px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100"
                    >
                      <svg className="inline-block mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Go to Website
                    </button>
                    
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        handleLogout();
                      }}
                      className="block px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100"
                    >
                      <svg className="inline-block mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Click outside to close dropdown */}
      {dropdownOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
}

export default Header;